import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import JerseyCard from "@/components/site/JerseyCard";
import { Search, X } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "nations", label: "Nations" },
  { id: "clubs", label: "Clubs" },
  { id: "home", label: "Home Kits" },
  { id: "away", label: "Away Kits" },
];

function classify(j) {
  const name = (j.name || "").toLowerCase();
  return {
    isNation: name.includes("world cup"),
    isClub: name.includes("jersey 26/27") || (!name.includes("world cup") && (j.player || "").toLowerCase().includes("club")),
    isHome: name.includes("home") || (!name.includes("away") && !name.includes("world cup")),
    isAway: name.includes("away"),
  };
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jerseys, setJerseys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [filter, setFilter] = useState(searchParams.get("f") || "all");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/jerseys");
        setJerseys(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  // Persist q + f to URL so links are shareable
  useEffect(() => {
    const next = new URLSearchParams();
    if (query.trim()) next.set("q", query.trim());
    if (filter !== "all") next.set("f", filter);
    setSearchParams(next, { replace: true });
  }, [query, filter, setSearchParams]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sortKey = (j) => {
      const team = (j.team || "").toLowerCase();
      const isAway = /away/i.test(j.name || "");
      return `${team}|${isAway ? "1" : "0"}`;
    };
    const list = jerseys.filter((j) => {
      // Text filter
      if (q) {
        const haystack = [j.name, j.player, j.team, j.season, j.description]
          .filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      // Category filter
      if (filter === "all") return true;
      const c = classify(j);
      if (filter === "nations") return c.isNation;
      if (filter === "clubs")   return c.isClub;
      if (filter === "home")    return c.isHome;
      if (filter === "away")    return c.isAway;
      return true;
    });
    // Sort alphabetically by team, with Home before Away within each team
    return [...list].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  }, [jerseys, query, filter]);

  const clearSearch = () => setQuery("");

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase">The Cabinet</div>
      <h1 className="font-display text-5xl md:text-6xl text-[#F2EBDD] mt-2">Classic Jerseys</h1>
      <p className="text-[#A8A39D] mt-3 max-w-2xl">Curated retro and modern football shirts. Selected by hand, shipped worldwide.</p>

      {/* Search + filters */}
      <div className="mt-10 border border-[#332D2B] bg-[#1C1816] p-5">
        <div className="flex items-center gap-3 border border-[#332D2B] bg-[#0F0C0B] px-4 focus-within:border-[#C8A96A] transition-colors">
          <Search size={18} className="text-[#A8A39D]" />
          <input
            data-testid="shop-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by team, country, player, season…"
            className="flex-1 bg-transparent border-0 outline-none py-3 text-[#F2EBDD] placeholder:text-[#756F6B] font-body"
          />
          {query && (
            <button
              data-testid="shop-search-clear"
              onClick={clearSearch}
              className="text-[#A8A39D] hover:text-[#C8A96A] p-1"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 border font-mono-retro text-[10px] tracking-[0.25em] uppercase transition-colors ${
                filter === f.id
                  ? "border-[#C8A96A] text-[#C8A96A] bg-[#0F0C0B]"
                  : "border-[#332D2B] text-[#A8A39D] hover:border-[#C8A96A] hover:text-[#F2EBDD]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Result count */}
      <div className="mt-6 flex items-center justify-between font-mono-retro text-[10px] tracking-[0.3em] uppercase text-[#A8A39D]">
        <span data-testid="shop-result-count">
          {loading ? "…" : `${filtered.length} ${filtered.length === 1 ? "shirt" : "shirts"}`}
          {query ? ` matching "${query}"` : ""}
        </span>
        {(query || filter !== "all") && !loading && (
          <button
            data-testid="shop-reset"
            onClick={() => { setQuery(""); setFilter("all"); }}
            className="text-[#C8A96A] hover:text-[#E6C875] tracking-[0.3em]"
          >
            Reset Filters
          </button>
        )}
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="font-mono-retro text-sm tracking-[0.2em] text-[#A8A39D] uppercase">Pressing the kits…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="shop-empty" className="border border-dashed border-[#332D2B] p-16 text-center">
            <div className="font-display text-3xl text-[#F2EBDD]">
              {jerseys.length === 0 ? "Cabinet Is Empty" : "No Shirts Match That Search"}
            </div>
            <p className="text-[#A8A39D] mt-3">
              {jerseys.length === 0
                ? "No shirts on the rail yet. The host will hang new fits soon."
                : "Try a different team, country, or clear the filters."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filtered.map((j) => <JerseyCard key={j.id} jersey={j} />)}
          </div>
        )}
      </div>
    </div>
  );
}
